import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

import { meQuery } from '../api/me.ts';
import { BrandIcon } from './brand.tsx';
import { icons } from './icons.ts';
import { useSession } from '../auth/session.tsx';

// The application frame every signed-in screen sits in, drawn from the Modernist design adopted on
// 2026-09-19: a 60px icon rail on the left, a top bar carrying today's date in the company's zone and
// who is signed in, and the screen beneath.
//
// The design's rail has twelve sections. This one has only the sections that exist: CB-REQ ADO-06
// prohibits navigation to capabilities that do not, and a rail icon leading nowhere is the "is it
// broken or am I doing it wrong" ADO-01 forbids. Each design section joins the rail with its slice.

const SECTIONS: ReadonlyArray<
  readonly [
    to:
      | '/'
      | '/quotes'
      | '/jobs'
      | '/invoices'
      | '/clients'
      | '/venues'
      | '/crew'
      | '/roster'
      | '/my-shifts'
      | '/inventory'
      | '/pricing'
      | '/documents'
      | '/insurance'
      | '/safety',
    label: string,
    icon: LucideIcon,
    /** Shown only to someone above the price line (FND-01): below it, the screen is a 403. */
    pricesOnly?: boolean,
    /** Shown only to someone who holds this permission: without it, the screen is a 403. */
    requires?: string,
  ]
> = [
  ['/', 'Home', icons.home],
  // CB-REQ CRW-08 at v1.964: a crew member's own shifts — the whole of the Crew role's rail.
  ['/my-shifts', 'My shifts', icons.shift, false, 'shifts.own'],
  // Each office section names its permission, so the Crew role sees none of them (ADO-06: no
  // rail icon leads to a 403).
  ['/quotes', 'Quotes', icons.quote, false, 'quotes.view'],
  ['/jobs', 'Jobs', icons.job, false, 'jobs.view'],
  ['/invoices', 'Invoices', icons.invoice, false, 'invoices.view'],
  ['/clients', 'Clients', icons.client, false, 'clients.view'],
  ['/venues', 'Venues', icons.venue, false, 'venues.view'],
  // CB-REQ CRW-01 and CRW-12 at v1.960: who works shows, and who is working when.
  ['/crew', 'Crew', icons.crew, false, 'crew.view'],
  ['/roster', 'Roster', icons.roster, false, 'shifts.view'],
  // CB-REQ CMP-02 at v1.967: SWMS, written here and signed by crew.
  ['/safety', 'SWMS', icons.swms, false, 'shifts.view'],
  ['/inventory', 'Inventory', icons.gear, false, 'inventory.view'],
  ['/pricing', 'Pricing', icons.pricing, true],
  // CB-REQ DOC-01 at v1.959: what documents print — terms, logo, contact and payment details.
  ['/documents', 'Documents', icons.documents, false, 'documents.manage'],
  // CB-REQ CMP-06 at v1.966: the company's certificates of currency against what venues ask.
  ['/insurance', 'Insurance', icons.insurance, false, 'documents.manage'],
];

export function Shell({ children }: { children: ReactNode }) {
  const session = useSession();
  const navigate = useNavigate();
  const me = useQuery(meQuery);

  async function signOut() {
    await session.signOut();
    await navigate({ to: '/sign-in' });
  }

  return (
    <div className="flex min-h-dvh bg-ground">
      <nav
        aria-label="Main"
        className="sticky top-0 flex h-dvh w-[60px] shrink-0 flex-col border-r-2 border-divider"
      >
        <Link
          to="/"
          aria-label="AVenue Rental — home"
          className="flex h-[60px] items-center justify-center border-b-2 border-divider bg-accent-600 text-white"
        >
          {/* Small UI, so the icon stands in for the wordmark (brand pack v1.1). */}
          <BrandIcon size={24} />
        </Link>
        <ul className="mt-3 flex flex-col">
          {SECTIONS.filter(
            ([, , , pricesOnly, requires]) =>
              (!pricesOnly || me.data?.canSeePrices) &&
              (!requires || me.data?.capabilities.includes(requires)),
          ).map(([to, label, Icon]) => (
            <li key={to}>
              <Link
                to={to}
                aria-label={label}
                title={label}
                // "/" would otherwise be active on every page.
                activeOptions={{ exact: to === '/' }}
                activeProps={{ 'aria-current': 'page' }}
                className="flex h-[46px] items-center justify-center border-l-[3px] border-transparent text-muted hover:bg-ink/5 hover:text-ink aria-[current=page]:border-accent aria-[current=page]:bg-accent-100 aria-[current=page]:text-ink"
              >
                <Icon size={21} strokeWidth={1.75} aria-hidden />
              </Link>
            </li>
          ))}
        </ul>
        <button
          type="button"
          onClick={() => void signOut()}
          aria-label="Sign out"
          title="Sign out"
          className="mt-auto mb-3 flex h-[46px] cursor-pointer items-center justify-center text-muted hover:bg-ink/5 hover:text-ink"
        >
          <icons.signOut size={20} strokeWidth={1.75} aria-hidden />
        </button>
      </nav>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 border-b-2 border-divider px-6 py-3">
          {/* No retyped product name here: the icon beside it already says whose app this is, and
              the wordmark never sits next to the icon (brand pack v1.1). */}
          <div className="flex flex-col">
            {me.data ? (
              <span className="text-sm text-ink-soft">{today(me.data.tenant.defaultTz)}</span>
            ) : null}
          </div>
          {me.data ? (
            <div className="flex items-center gap-3">
              <div className="flex flex-col items-end border-l-2 border-divider pl-4 text-right">
                <span className="text-[13px] font-semibold text-ink">{me.data.tenant.name}</span>
                <span className="text-xs text-muted">{me.data.displayName}</span>
              </div>
              <span
                aria-hidden
                className="flex h-8 w-8 items-center justify-center bg-ink text-xs font-extrabold text-ground"
              >
                {initials(me.data.displayName)}
              </span>
            </div>
          ) : null}
        </header>
        <main className="mx-auto w-full max-w-6xl p-6">{children}</main>
      </div>
    </div>
  );
}

/**
 * Today in the company's zone, named. The zone is passed explicitly: a formatter that defaults it is
 * prohibited (CB-CONV §3.2, §7), because "today" at 11pm in Perth is tomorrow in Melbourne.
 */
function today(timeZone: string): string {
  const date = new Intl.DateTimeFormat('en-AU', {
    timeZone,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date());
  return `${date} · ${timeZone}`;
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]!.toUpperCase())
    .join('');
}
